import { useEffect, useRef, useState } from 'react';

interface SvgContentBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SvgBoundsState {
  contentBounds: SvgContentBounds;
  viewBoxWidth: number;
  viewBoxHeight: number;
}

function readBoundsFromSvg(svg: SVGSVGElement): SvgBoundsState | null {
  const bbox = svg.getBBox();
  if (!bbox.width || !bbox.height) return null;

  const viewBox = svg.getAttribute('viewBox');
  const [, , vbWidth, vbHeight] = viewBox
    ? viewBox.split(' ').map(Number)
    : [0, 0, bbox.width, bbox.height];

  return {
    contentBounds: {
      x: bbox.x,
      y: bbox.y,
      width: bbox.width,
      height: bbox.height,
    },
    viewBoxWidth: vbWidth,
    viewBoxHeight: vbHeight,
  };
}

export const useSvgContentBounds = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<SvgBoundsState | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tryRead = (): boolean => {
      const svg = container.querySelector('svg');
      if (svg) {
        const bounds = readBoundsFromSvg(svg as SVGSVGElement);
        if (bounds) {
          setState(bounds);
          return true;
        }
      }
      return false;
    };

    if (tryRead()) return;

    const observer = new MutationObserver(() => {
      if (tryRead()) observer.disconnect();
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return { containerRef, ...state };
};
